import { gsap } from "gsap";
import event from "../lib/mixins/event";

// Virtual px of scroll (wheel/touch) needed per second of video — tune to control scrub speed
const SCROLL_PX_PER_SECOND = 150;
// Progress bar starts partially filled so there's something to see immediately
const PROGRESS_MIN_PERCENT = 10;

class ScrollVideo {
	constructor(dom) {
		this.NAME = "ScrollVideo";
		this.dom = dom; // .scroll-video-wrap
		this.media = dom.querySelector(".scroll-video__media");
		this.video = dom.querySelector("[data-scrub-video]");
		this.playButton = dom.querySelector("[data-sv-play]");
		this.preloader = dom.querySelector("[data-sv-preloader]");
		this.fillBar = dom.querySelector("[data-sv-fill-bar]");

		const outer = dom.parentElement;
		this.modal = outer ? outer.querySelector("[data-sv-modal]") : null;
		this.modalStage = this.modal ? this.modal.querySelector("[data-sv-modal-stage]") : null;
		this.progressBar = this.modal ? this.modal.querySelector("[data-sv-progress-bar]") : null;
		this.closeButton = this.modal ? this.modal.querySelector("[data-sv-close]") : null;

		this._ready = false;
		this._open = false;
		this._scrollProgress = 0; // 0–1
		this._touchY = 0;

		this._onWheel = this._onWheel.bind(this);
		this._onTouchStart = this._onTouchStart.bind(this);
		this._onTouchMove = this._onTouchMove.bind(this);
		this._onKeydown = this._onKeydown.bind(this);
	}

	init() {
		if (!this.video || !this.playButton || !this.modal) return;

		// Move the modal to <body> so it escapes any ancestor stacking context
		document.body.appendChild(this.modal);

		this.playButton.addEventListener("click", () => this._handlePlayClick());

		if (this.closeButton) {
			this.closeButton.addEventListener("click", () => this.close());
		}
	}

	_handlePlayClick() {
		this.playButton.style.display = "none";

		// Already downloaded from a previous play — reopen without re-fetching
		if (this._ready) {
			this._openModal();
			return;
		}

		this._preload();
	}

	_preload() {
		const isMobile = !window.matchMedia("(min-width: 1024px)").matches;
		const mobileSrc = this.video.dataset.videoSrcMobile;
		const src = (isMobile && mobileSrc) ? mobileSrc : this.video.dataset.videoSrc;

		if (this.preloader) this.preloader.style.display = "flex";

		if (!src) {
			// No src on data attr — fall back to immediate setup
			this._hidePreloader();
			return;
		}

		const xhr = new XMLHttpRequest();
		xhr.open("GET", src, true);
		xhr.responseType = "blob";

		xhr.onprogress = (e) => {
			if (e.lengthComputable && this.fillBar) {
				this.fillBar.style.height = `${(e.loaded / e.total) * 100}%`;
			}
		};

		xhr.onload = () => {
			if (xhr.status !== 200) {
				this._hidePreloader();
				return;
			}

			// Snap fill to 100% so users see completion before fade-out
			if (this.fillBar) this.fillBar.style.height = "100%";

			const blobUrl = URL.createObjectURL(xhr.response);
			this.video.src = blobUrl;

			// canplaythrough fires once the browser has buffered enough to play
			this.video.addEventListener("canplaythrough", () => this._hidePreloader(), {
				once: true,
			});

			// Fallback: if canplaythrough never fires (edge case), proceed after 2s
			setTimeout(() => this._hidePreloader(), 2000);
		};

		xhr.onerror = () => this._hidePreloader();

		xhr.send();
	}

	_hidePreloader() {
		// Guard — only run once (canplaythrough + timeout may both fire)
		if (this._ready) return;
		this._ready = true;

		if (!this.preloader) {
			this._openModal();
			return;
		}

		gsap.to(this.preloader, {
			opacity: 0,
			duration: 0.6,
			delay: 0.3, // brief pause at 100% fill before fading out
			ease: "power2.inOut",
			onComplete: () => {
				this.preloader.style.display = "none";
				this.preloader.style.opacity = "";
				this._openModal();
			},
		});
	}

	_openModal() {
		const video = this.video;

		// iOS Safari won't decode frames via currentTime until play() has been called
		video.play().then(() => video.pause()).catch(() => {});

		this.modalStage.appendChild(video);
		video.style.opacity = "1";
		video.currentTime = 0;

		this._scrollProgress = 0;
		this._setProgress(0);

		this.modal.classList.add("scroll-video-modal--active");
		document.body.classList.add("no-scroll");
		this._open = true;

		this.modal.addEventListener("wheel", this._onWheel, { passive: false });
		this.modal.addEventListener("touchstart", this._onTouchStart, { passive: true });
		this.modal.addEventListener("touchmove", this._onTouchMove, { passive: false });
		document.addEventListener("keydown", this._onKeydown);
	}

	close() {
		if (!this._open) return;
		this._open = false;

		this.modal.classList.remove("scroll-video-modal--active");
		document.body.classList.remove("no-scroll");

		this.modal.removeEventListener("wheel", this._onWheel);
		this.modal.removeEventListener("touchstart", this._onTouchStart);
		this.modal.removeEventListener("touchmove", this._onTouchMove);
		document.removeEventListener("keydown", this._onKeydown);

		this.video.pause();

		// Return the video to its placeholder spot, ready for a replay
		if (this.media) this.media.appendChild(this.video);
		this.video.style.opacity = "0";
		this.playButton.style.display = "";
	}

	_onWheel(e) {
		e.preventDefault();
		this._advanceScroll(e.deltaY);
	}

	_onTouchStart(e) {
		this._touchY = e.touches[0].clientY;
	}

	_onTouchMove(e) {
		e.preventDefault();
		const y = e.touches[0].clientY;
		this._advanceScroll(this._touchY - y);
		this._touchY = y;
	}

	_onKeydown(e) {
		if (e.key === "Escape") this.close();
	}

	_advanceScroll(deltaY) {
		if (!this.video.duration) return;

		const scrollRange = this.video.duration * SCROLL_PX_PER_SECOND;
		this._scrollProgress = gsap.utils.clamp(0, 1, this._scrollProgress + deltaY / scrollRange);

		this.video.currentTime = this._scrollProgress * this.video.duration;
		this._setProgress(this._scrollProgress);
	}

	_setProgress(progress) {
		if (!this.progressBar) return;
		const percent = PROGRESS_MIN_PERCENT + progress * (100 - PROGRESS_MIN_PERCENT);
		this.progressBar.style.width = `${percent}%`;
	}

	destroy() {
		this.close();
	}
}

Object.assign(ScrollVideo.prototype, event);
export default ScrollVideo;
